import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Layers, Target } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Select } from '../../../../../components/ui/Select';
import { GROUPS, type GroupId } from '../../../../../config/systemConfig';
import type { PracticeContentHealthReport, PracticeCapacityStatus } from '../hooks/useGovernanceData';
interface PracticeContentHealthTabProps {
  report: PracticeContentHealthReport;
  isRunning: boolean;
}
const STATUS_META: Record<PracticeCapacityStatus, {
  label: string;
  className: string;
}> = {
  critical: {
    label: 'Kritiek',
    className: 'bg-red-50 text-red-700 border-red-200'
  },
  thin: {
    label: 'Dun',
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  healthy: {
    label: 'Gezond',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
export function PracticeContentHealthTab({
  report,
  isRunning
}: PracticeContentHealthTabProps) {
  const [groupFilter, setGroupFilter] = useState<'all' | GroupId>('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const modules = useMemo(() => Array.from(new Set(report.modules.map(item => item.module))).sort(), [report.modules]);
  const visibleFamilies = report.families.filter(family => {
    if (groupFilter !== 'all' && family.group !== groupFilter) return false;
    return moduleFilter === 'all' || family.module === moduleFilter;
  });
  const visibleModules = report.modules.filter(item => {
    if (groupFilter !== 'all' && item.group !== groupFilter) return false;
    return moduleFilter === 'all' || item.module === moduleFilter;
  });
  if (isRunning && report.modules.length === 0) {
    return <div className="p-8 text-sm text-gray-500" data-id="element-5445" data-mp="xlis1t3cnqhg">Oefenstof analyseren…</div>;
  }
  return <div className="space-y-6" data-id="element-5446" data-mp="2dzsgsobi7h8t">
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-id="element-5447" data-mp="28yux27auif57">
        <div className="flex items-start gap-4" data-id="element-5448" data-mp="290xq707aooe5">
          <div className="rounded-xl bg-indigo-100 p-3 text-indigo-700" data-id="element-5449" data-mp="2g0rjqdimjza3">
            <Layers className="h-6 w-6" data-id="element-5450" data-mp="2l0cqha41dbxd" />
          </div>
          <div data-id="element-5451" data-mp="2l2yzle6krzhp">
            <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700" data-id="element-5452" data-mp="2pzz59nzij08a">
              Practice capacity
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-900" data-id="element-5453" data-mp="2uy0nk6p46vip">
              Oefenstof-capaciteit
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600" data-id="element-5454" data-mp="1xcuvl5ph5xq6">
              Waar kan adaptief oefenen nog veilig variëren? Dit meet bruikbare,
              unieke vragen per family binnen de tiers van een groep — geen
              mastery-score en geen blokkade voor leerlingen.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-5455" data-mp="1elbsyzzd4loo">
        <Metric label="Kritieke families" value={report.counts.critical} tone="red" data-id="element-5456" data-mp="1eipm72kaqa7i" />
        <Metric label="Dunne families" value={report.counts.thin} tone="amber" data-id="element-5457" data-mp="oilhhyt70fj" />
        <Metric label="Gezonde families" value={report.counts.healthy} tone="emerald" data-id="element-5458" data-mp="3ssbdj9vj3n32" />
      </div>

      <Card className="p-4" data-id="element-5459" data-mp="3nrncdvdl5pv4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-5460" data-mp="3iudr8rx8c0d3">
          <Select value={String(groupFilter)} onChange={event => setGroupFilter(event.target.value === 'all' ? 'all' : Number(event.target.value) as GroupId)} options={[{
          value: 'all',
          label: 'Alle groepen'
        }, ...GROUPS.map(group => ({
          value: String(group),
          label: `Groep ${group}`
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5461" data-mp="3iu5f528xkz2y" />
          <Select value={moduleFilter} onChange={event => setModuleFilter(event.target.value)} options={[{
          value: 'all',
          label: 'Alle modules'
        }, ...modules.map(module => ({
          value: module,
          label: module
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5462" data-mp="u2bh581zxl9s" />
          <p className="text-xs text-slate-500 sm:ml-auto" data-id="element-5463" data-mp="gawzp5nqls4u">
            Kritiek: 0–3 vragen · dun: 4–6 · gezond: 7+
          </p>
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-5464" data-mp="1fh3ov3ljm5t">
        <div className="border-b border-slate-100 p-5" data-id="element-5465" data-mp="6cqxemy0qjzg">
          <h3 className="text-sm font-semibold text-slate-900" data-id="element-5466" data-mp="6d93ebahgckk">
            Praktijkruimte per groep × module
          </h3>
          <p className="mt-1 text-xs text-slate-500" data-id="element-5467" data-mp="1vq494x8gnaal">
            Rode combinaties bevatten families die direct tot fallback,
            herhaling of leerdoel-drift kunnen leiden.
          </p>
        </div>
        <div className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-3" data-id="element-5468" data-mp="3g9zm2d8g4f93">
          {visibleModules.map(item => {
          const meta = STATUS_META[item.status];
          return <div key={`${item.group}-${item.module}`} className="bg-white p-4" data-id="element-5469" data-mp="3q85msriinfcd">
                <div className="flex items-center justify-between gap-2" data-id="element-5470" data-mp="7tei5auo8ccu">
                  <p className="font-mono text-xs font-bold text-slate-800" data-id="element-5471" data-mp="3suls9hx9crr">
                    G{item.group} · {item.module}
                  </p>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${meta.className}`} data-id="element-5472" data-mp="hrtzim3wc795">
                    {meta.label}
                  </span>
                </div>
                <p className="mt-3 text-2xl font-bold tabular-nums text-slate-900" data-id="element-5473" data-mp="2jljt0764nnr8">
                  {item.totalQuestions}
                </p>
                <p className="text-xs text-slate-500" data-id="element-5474" data-mp="ii99594xbgy2">
                  vragen · {item.familiesWithContent}/{item.eligibleFamilies}{' '}
                  families met inhoud
                </p>
                <p className="mt-2 text-xs text-slate-600" data-id="element-5475" data-mp="rhw8lpe3ecoh">
                  {item.criticalFamilies} kritiek · {item.thinFamilies} dun
                </p>
              </div>;
        })}
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-5476" data-mp="11ftx8dzv6bhm">
        <div className="flex items-start gap-3 border-b border-slate-100 p-5" data-id="element-5477" data-mp="16fgauojlttqy">
          <Target className="mt-0.5 h-4 w-4 text-red-500" data-id="element-5478" data-mp="16dbuv88fl4ab" />
          <div data-id="element-5479" data-mp="1b77suaop2r5h">
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-5480" data-mp="1b6olfnwo7l5p">
              Auteurprioriteit
            </h3>
            <p className="mt-1 text-xs text-slate-500" data-id="element-5481" data-mp="1f6ys72saeflm">
              Kritiek vraagt eerst om inhoud. Dunne families zijn daarna
              uitbreidingswerk: zeven unieke varianten is de minimumbasis; een
              gerichte sessie van tien vragen vraagt nog steeds om herhaling.
            </p>
          </div>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-5482" data-mp="1uarsybd2utpd">
          {visibleFamilies.filter(family => family.status !== 'healthy').map(family => {
          const meta = STATUS_META[family.status];
          return <div key={family.key} className="flex items-center gap-3 p-4" data-id="element-5483" data-mp="1lbo7j0qfu27p">
                  {family.status === 'critical' ? <AlertTriangle className="h-4 w-4 flex-none text-red-500" data-id="element-5484" data-mp="2d19ffy7bgt2a" /> : <AlertTriangle className="h-4 w-4 flex-none text-amber-500" data-id="element-5485" data-mp="28aj8whn5mk60" />}
                  <div className="min-w-0 flex-1" data-id="element-5486" data-mp="248xpsek119nd">
                    <p className="font-mono text-xs font-semibold text-slate-900" data-id="element-5487" data-mp="248fxft0hjfkq">
                      G{family.group} · {family.familyId}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-id="element-5488" data-mp="2vzlozl2544ye">
                      {family.questionCount} bruikbare vragen
                      {family.questionTypes.length > 0 && ` · ${family.questionTypes.join(', ')}`}
                    </p>
                    {family.sessionRepeatCount > 0 && <p className="mt-1 text-xs text-slate-500" data-id="element-5489" data-mp="35razjzc6sgb1">
                        In een focusset van 10: minimaal{' '}
                        {family.sessionRepeatCount} herhaling
                        {family.sessionRepeatCount === 1 ? '' : 'en'} zonder
                        nieuwe varianten
                      </p>}
                  </div>
                  <span className={`rounded-full border px-2 py-1 text-[10px] font-bold ${meta.className}`} data-id="element-5490" data-mp="3oh925zx7th7a">
                    +{family.questionsNeeded} naar minimum
                  </span>
                </div>;
        })}
          {visibleFamilies.every(family => family.status === 'healthy') && <div className="flex items-center gap-2 p-6 text-sm text-emerald-700" data-id="element-5491" data-mp="2hy16gbmgjcns">
              <CheckCircle2 className="h-5 w-5" data-id="element-5492" data-mp="104vcw4sijwkq" />
              Geen dunne of kritieke families binnen deze selectie.
            </div>}
        </div>
      </Card>
    </div>;
}
function Metric({
  label,
  value,
  tone
}: {
  label: string;
  value: number;
  tone: 'red' | 'amber' | 'emerald';
}) {
  const colors = {
    red: 'border-red-100 bg-red-50 text-red-700',
    amber: 'border-amber-100 bg-amber-50 text-amber-700',
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700'
  }[tone];
  return <div className={`rounded-xl border p-4 ${colors}`} data-id="element-5493" data-mp="q4kyiguqsj3z">
      <p className="text-2xl font-bold tabular-nums" data-id="element-5494" data-mp="q65r40xhxrz7">{value}</p>
      <p className="mt-1 text-xs font-medium" data-id="element-5495" data-mp="ldcrfrgvx9tz">{label}</p>
    </div>;
}